import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation, Navigate } from "react-router-dom";
import { C, FIRMS, getFirmVisitHref } from "./firmsData";
import FIRM_SUMMARIES from "./firmSummaries";
export { FIRMS } from "./firmsData";


function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= breakpoint);
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener("change", handler);
    setIsMobile(mql.matches);
    return () => mql.removeEventListener("change", handler);
  }, [breakpoint]);
  return isMobile;
}

const PAGE_BG = "radial-gradient(ellipse at 50% 0%, rgba(30, 58, 138, 0.22) 0%, transparent 65%), #0a0c14";
const CARD_BG = "#0f1117";  // matches side drawer
const TABLE_PANEL_BG = "#05070e"; // firm detail data tables (darker than surrounding content)
const TABLE_HEADER_BG = "rgba(255,255,255,0.055)"; // header row on table panels


const isDrawdownFavorable = (d) => d && (d.toLowerCase().includes("eod") && !d.toLowerCase().startsWith("intraday"));
const isSplitFavorable = (s) => s && (s.includes("100%") || s.includes("90%"));
const badgeStyles = { positive: { color: "#00B67A" }, negative: { color: "#f87171" }, neutral: { color: "#94a3b8" } };

const MONO = "'Inter', ui-sans-serif, system-ui, sans-serif";

const shortDrawdown = (d) => {
  if (!d) return "—";
  const l = d.toLowerCase();
  if (l.includes("dynamic")) return "EOD Dynamic";
  if (l.includes("static") && l.includes("eod") && l.includes("intraday")) return "3 Options";
  if (l.includes("varies") || l.includes("→")) return "Varies by phase";
  if (l.includes("eod") && l.includes("intraday")) return "EOD / Intraday";
  if (l.includes("eod")) return "EOD Trailing";
  if (l.includes("intraday")) return "Intraday";
  return d.length > 16 ? d.slice(0, 14) + "…" : d;
};

const shortSplit = (s) => {
  if (!s) return "—";
  if (s.includes("100%")) return "100%";
  const m = s.match(/^(\d+[–-]?\d*%)/);
  if (m) return m[1];
  return s.length > 10 ? s.slice(0, 8) + "…" : s;
};

const TrustpilotStars = ({ rating }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    const fill = rating >= i ? 1 : rating >= i - 0.5 ? 0.5 : 0;
    stars.push(
      <svg key={i} width="14" height="14" viewBox="0 0 24 24" style={{ display:"block" }}>
        <defs>
          <linearGradient id={`star-grad-${i}-${rating}`}>
            <stop offset={fill === 1 ? "100%" : "50%"} stopColor="#00B67A" />
            <stop offset={fill === 1 ? "100%" : "50%"} stopColor={C.borderLight} />
          </linearGradient>
        </defs>
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
          fill={fill === 0 ? C.borderLight : `url(#star-grad-${i}-${rating})`} />
      </svg>
    );
  }
  return (
    <div style={{ display:"flex", alignItems:"center", gap:3 }}>
      {stars}
      <span style={{ fontFamily:MONO, fontSize:12, fontWeight:600, color:C.textMuted, marginLeft:4 }}>{rating.toFixed(1)}</span>
      <span style={{ fontSize:10, color:C.textDim, marginLeft:4 }}>Trustpilot</span>
    </div>
  );
};

const getStatVariant = (label, value, firm) => {
  if (label === "DRAWDOWN") return isDrawdownFavorable(value) ? "positive" : "neutral";
  if (label === "PROFIT SPLIT") return isSplitFavorable(value) ? "positive" : "neutral";
  if (label === "DAILY LOSS") return firm.hasDLL ? "negative" : "positive";
  return "neutral";
};


const SECTION_META = {
  "Account Info": {
    subtitle: "Drawdown behavior, scaling, account limits",
    icon: (color) => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill={color} stroke="none">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
      </svg>
    ),
  },
  "Payout Rules": {
    subtitle: "Split, schedule, limits, processing",
    icon: (color) => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill={color} stroke="none">
        <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
      </svg>
    ),
  },
  "Trading Restrictions": {
    subtitle: "News, prohibited strategies, position rules",
    icon: (color) => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill={color} stroke="none">
        <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
      </svg>
    ),
  },
  "Account Killers": {
    subtitle: "Instant breach, inactivity, rule violations",
    icon: (color) => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill={color} stroke="none">
        <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
      </svg>
    ),
  },
};

const TSCollapsible = ({ title, dot, items, forceOpen, id, subtitle, icon }) => {
  const [open, setOpen] = useState(false);
  const mobile = useIsMobile();
  useEffect(() => { if (forceOpen) setOpen(true); }, [forceOpen]);
  return (
    <div id={id} style={{ border:"1px solid rgba(255,255,255,0.06)", borderRadius:10, overflow:"hidden", width:"100%", transition:"border-color 0.2s, box-shadow 0.2s" }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(59,130,246,0.35)"; e.currentTarget.style.boxShadow = "0 0 0 1px rgba(59,130,246,0.12), 0 0 16px rgba(59,130,246,0.08)"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; e.currentTarget.style.boxShadow = "none"; }}
    >
      <button onClick={() => setOpen(o => !o)} style={{
        width:"100%", display:"flex", alignItems:"center", gap:12, padding:"16px 18px",
        background:CARD_BG, border:"none", cursor:"pointer", fontFamily:"inherit",
      }}>
        <span style={{ flexShrink:0, display:"flex", alignItems:"center", opacity:0.85 }}>
          {icon ? icon(dot) : (
            <span style={{ width:8, height:8, borderRadius:"50%", background:dot, display:"block" }} />
          )}
        </span>
        <span style={{ flex:1, textAlign:"left", display:"flex", alignItems:"baseline", gap:8, flexWrap:"wrap" }}>
          <span style={{ fontSize: mobile ? 15 : 16, fontWeight:600, color:C.text }}>{title}</span>
          {subtitle && (
            <>
              <span style={{ color:C.textDim, fontSize: mobile ? 11 : 12 }}>–</span>
              <span style={{ fontSize: mobile ? 11 : 12, color:C.textDim, fontWeight:400 }}>{subtitle}</span>
            </>
          )}
        </span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.textDim} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{ flexShrink:0, transform: open ? "rotate(180deg)" : "rotate(0deg)", transition:"transform 0.25s ease" }}>
          <path d="m6 9 6 6 6-6"/>
        </svg>
      </button>
      {open && (
        <div style={{ padding:"0 16px 16px", background:CARD_BG }}>
          {items.length > 0 && typeof items[0] === "object" && items[0].bold !== undefined ? (
            <div style={{ paddingTop:4 }}>
              {items.map((item, i) => (
                <div key={i} style={{ marginBottom: i < items.length - 1 ? 18 : 0 }}>
                  <div style={{ fontWeight:700, fontSize: mobile ? 14 : 15, color:C.text, marginBottom:4 }}>{item.bold}</div>
                  {item.lines.map((line, j) => (
                    <div key={j} style={{ fontSize: mobile ? 14 : 15, color:C.textMuted, lineHeight:1.7 }}>{line}</div>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <ul style={{ margin:0, padding:"0 0 0 18px", listStyle:"disc" }}>
              {items.map((item, i) => (
                <li key={i} style={{ fontSize: mobile ? 14 : 15, color:C.textMuted, lineHeight:1.7, marginBottom:2 }}>{item}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

const TSPhaseTable = ({ title, rows, sizes, accent }) => {
  const mobile = useIsMobile();
  const bar = accent || "linear-gradient(180deg, #60a5fa 0%, #1e3a8a 100%)";
  return (
  <div style={{ flex:1, minWidth:280 }}>
    <h4 style={{ margin:"0 0 10px", fontSize: mobile ? 19 : 21, fontWeight:700, color:C.text }}>{title}</h4>
    <div style={{ border:"1px solid rgba(255,255,255,0.06)", borderRadius:10, overflow:"hidden", position:"relative", background:TABLE_PANEL_BG }}>
      <div style={{ position:"absolute", left:0, top:0, bottom:0, width:3, background:bar, zIndex:1, borderRadius:"10px 0 0 10px" }} />
      <div style={{ overflowX:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", minWidth:320, background:TABLE_PANEL_BG }}>
          <thead>
            <tr style={{ background:TABLE_HEADER_BG }}>
              <th style={{ padding:"14px 14px", textAlign:"left", fontSize: mobile ? 13 : 14, fontWeight:600, color:C.textDim, textTransform:"uppercase", letterSpacing:"0.04em" }}>Account size</th>
              {sizes.map(s => (
                <th key={s} style={{ padding:"14px 14px", textAlign:"center", fontSize: mobile ? 14 : 15, fontWeight:700, color:C.text, fontVariantNumeric:"tabular-nums" }}>{s}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const isContractRow = /contract|position/i.test(r.label);
              return (
              <tr key={i} style={{ borderTop:"1px solid rgba(255,255,255,0.06)" }}>
                <td style={{ padding:"14px 14px", fontSize: mobile ? 13 : 14, color:C.blue, fontWeight:600 }}>{r.label}</td>
                {r.values.map((v, j) => {
                  const display = (isContractRow && /^\d+$/.test(v)) ? `${v} Minis` : v;
                  const color = /max payout/i.test(r.label)
                    ? C.green
                    : v === "None"
                      ? C.textDim
                      : C.text;
                  return (
                    <td key={j} style={{ padding:"14px 14px", textAlign:"center", fontSize: mobile ? 14 : 15, fontWeight:500, fontVariantNumeric:"tabular-nums lining-nums", color }}>{display}</td>
                  );
                })}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  </div>
  );
};

const FirmDetail = ({ firm, onBack, initialProductName, initialSection }) => {
  const mobile = useIsMobile();
  const [headerH, setHeaderH] = useState(0);
  useEffect(() => {
    const measure = () => setHeaderH(document.querySelector('header')?.offsetHeight || 0);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  const [tab, setTab] = useState(() => {
    if (initialProductName) {
      const idx = firm.products.findIndex(p => p.name === initialProductName);
      return idx >= 0 ? idx : 0;
    }
    return 0;
  });
  const [compareOpen, setCompareOpen] = useState(false);
  const [aiSummaryOpen, setAiSummaryOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("section-overview");
  const [forceOpenSection, setForceOpenSection] = useState(null);
  const product = firm.products[tab] || firm.products[0];
  const sections = product.sections || [];
  const summary = FIRM_SUMMARIES[firm.id];

  const navItems = [];
  navItems.push({ id: "section-overview", label: "Firm Overview" });
  if (product.pricing && product.pricing.length > 0) navItems.push({ id: "section-pricing", label: "Pricing" });
  if (product.evalTable) navItems.push({ id: "section-eval", label: product.evalTable.title });
  if (product.fundedTable) navItems.push({ id: "section-funded", label: product.fundedTable.title });
  const sectionOrder = ["Account Info", "Payout Rules", "Trading Restrictions", "Account Killers"];
  for (const title of sectionOrder) {
    if (sections.find(s => s.title === title)) {
      const slug = title.toLowerCase().replace(/\s+/g, "-");
      navItems.push({ id: `section-${slug}`, label: title });
    }
  }

  useEffect(() => {
    if (!initialSection) return;
    const accordionTitles = ["Account Info", "Payout Rules", "Trading Restrictions", "Account Killers"];
    if (accordionTitles.includes(initialSection)) {
      setForceOpenSection(initialSection);
    }
    setTimeout(() => {
      const slug = initialSection.toLowerCase().replace(/\s+/g, "-");
      const el = document.getElementById(`section-${slug}`);
      if (el) {
        const offset = mobile ? headerH + 100 : 40;
        const y = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }, 200);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const ids = navItems.map(n => n.id);
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        }
      },
      { threshold: 0.1, rootMargin: "-20% 0px -60% 0px" }
    );
    ids.forEach(id => { const el = document.getElementById(id); if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNavClick = (item) => {
    const accordionTitles = ["Account Info", "Payout Rules", "Trading Restrictions", "Account Killers"];
    if (accordionTitles.includes(item.label)) setForceOpenSection(item.label);
    setTimeout(() => {
      const el = document.getElementById(item.id);
      if (el) {
        if (mobile) {
          const y = el.getBoundingClientRect().top + window.scrollY - (headerH + 100);
          window.scrollTo({ top: y, behavior: "smooth" });
        } else {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    }, 50);
  };

  useEffect(() => {
    if (!mobile) return;
    const activeBtn = document.querySelector(`.hide-scrollbar button[data-section="${activeSection}"]`);
    if (activeBtn) activeBtn.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [activeSection, mobile]);

  const stats = [
    { label:"Drawdown", value: shortDrawdown(firm.drawdownType), variant: isDrawdownFavorable(firm.drawdownType) ? "positive" : "negative" },
    { label:"Profit Split", value: shortSplit(firm.profitSplit), variant: isSplitFavorable(firm.profitSplit) ? "positive" : "neutral" },
    { label:"News Trading", value: firm.newsTrading ? "Allowed" : "Blocked", variant: firm.newsTrading ? "positive" : "negative" },
    { label:"Daily Loss Limit", value: firm.hasDLL ? "Yes" : "None", variant: firm.hasDLL ? "negative" : "positive" },
    { label:"Max Funded", value: firm.maxFundedAccts || "—", variant:"neutral" },
    { label:"Max Payout", value: firm.maxPayout || "—", variant:"neutral" },
    { label:"Days to Pass", value: firm.daysToPass || "—", variant: firm.daysToPass === "1 day" ? "positive" : "neutral" },
  ];
  const cols = mobile ? 3 : 4;
  const visitHref = getFirmVisitHref(firm);

  return (
    <div style={{ minHeight:"100vh", background:PAGE_BG, color:C.text }}>
      <div style={{ maxWidth:1200, margin:"0 auto", padding: mobile ? "0 16px 48px" : "32px 40px 64px" }}>

        {/* BACK */}
        {mobile ? (
          <>
            <style>{`.hide-scrollbar::-webkit-scrollbar{display:none}.hide-scrollbar{-ms-overflow-style:none;scrollbar-width:none}`}</style>
            <div style={{ position:"sticky", top:headerH, zIndex:50, background:"rgba(10,12,20,0.92)", backdropFilter:"blur(12px)", margin:"0 -16px", padding:"12px 16px", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
              <button onClick={onBack} style={{ display:"inline-flex", alignItems:"center", gap:6, background:"none", border:"none", color:C.textMuted, fontSize:15, fontWeight:500, cursor:"pointer", fontFamily:"inherit", padding:0 }}
                onMouseEnter={e => e.currentTarget.style.color = C.text}
                onMouseLeave={e => e.currentTarget.style.color = C.textMuted}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
                All firms
              </button>
            </div>
            {navItems.length > 0 && (
              <div
                className="hide-scrollbar"
                style={{
                  position:"sticky", top:headerH + 45, zIndex:49,
                  background:"rgba(10,12,20,0.95)", backdropFilter:"blur(12px)",
                  borderBottom:"1px solid rgba(255,255,255,0.06)",
                  margin:"0 -16px", padding:"8px 16px",
                  overflowX:"auto", overflowY:"hidden",
                  WebkitOverflowScrolling:"touch",
                  display:"flex", gap:6,
                }}
              >
                {navItems.map(item => (
                  <button
                    key={item.id}
                    data-section={item.id}
                    onClick={() => handleNavClick(item)}
                    style={{
                      flexShrink:0, padding:"6px 14px", borderRadius:20,
                      border: activeSection === item.id ? "1px solid #3b82f6" : "1px solid rgba(255,255,255,0.08)",
                      background: activeSection === item.id ? "rgba(59,130,246,0.15)" : "transparent",
                      color: activeSection === item.id ? "#3b82f6" : "#8b949e",
                      fontSize:12, fontWeight: activeSection === item.id ? 600 : 500,
                      cursor:"pointer", fontFamily:"inherit",
                      whiteSpace:"nowrap", transition:"all 0.15s",
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </>
        ) : null}

        {/* TWO-COLUMN WRAPPER */}
        <div style={{ display: mobile ? "block" : "flex", alignItems:"flex-start" }}>

          {/* LEFT NAV — desktop only */}
          {!mobile && (
            <nav style={{
              width:180, flexShrink:0, position:"sticky", top:52,
              alignSelf:"flex-start", paddingTop:8, paddingRight:24,
              borderRight:"1px solid rgba(255,255,255,0.06)",
            }}>
              <button onClick={onBack} style={{ display:"inline-flex", alignItems:"center", gap:6, background:"none", border:"none", color:C.textMuted, fontSize:14, fontWeight:500, cursor:"pointer", fontFamily:"inherit", padding:"8px 12px", marginBottom:12, width:"100%" }}
                onMouseEnter={e => e.currentTarget.style.color = C.text}
                onMouseLeave={e => e.currentTarget.style.color = C.textMuted}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
                All firms
              </button>
              {navItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item)}
                  style={{
                    display:"block", width:"100%", textAlign:"left",
                    padding:"8px 12px", marginBottom:2,
                    background:"none", border:"none",
                    borderLeft: activeSection === item.id ? "2px solid #3b82f6" : "2px solid transparent",
                    color: activeSection === item.id ? C.text : C.textMuted,
                    fontSize:13, fontWeight: activeSection === item.id ? 600 : 400,
                    cursor:"pointer", fontFamily:"inherit",
                    transition:"color 0.15s, border-color 0.15s",
                    lineHeight:1.4,
                  }}
                  onMouseEnter={e => { if (activeSection !== item.id) e.currentTarget.style.color = C.text; }}
                  onMouseLeave={e => { if (activeSection !== item.id) e.currentTarget.style.color = C.textMuted; }}
                >
                  {item.label}
                </button>
              ))}
            </nav>
          )}

          {/* CONTENT */}
          <div style={{ flex:1, minWidth:0, paddingLeft: mobile ? 0 : 32, paddingTop: mobile ? 16 : 0 }}>

            {/* HEADER + STATS — section-overview */}
            <div id="section-overview">
              <div style={{ display:"flex", alignItems:"flex-start", gap: mobile ? 14 : 20, marginTop: mobile ? 16 : 0, marginBottom: mobile ? 24 : 32 }}>
                <img src={firm.logo} alt="" style={{ width:64, height:64, borderRadius:14, objectFit:"contain", background:"rgba(255,255,255,0.06)", padding:8, flexShrink:0 }} onError={e=>{e.target.onerror=null;e.target.style.display="none";}} />
                <div>
                  <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                    <h1 style={{ margin:0, fontSize: mobile ? 24 : 34, fontWeight:700, letterSpacing:"-0.02em" }}>{firm.name}</h1>
                    {firm.regulated && firm.regulator && <span style={{ fontSize: mobile ? 11 : 12, fontWeight:600, background:C.greenBg, color:C.green, padding:"3px 10px", borderRadius:4 }}>NFA</span>}
                  </div>
                  <div style={{ marginTop:6, display:"flex", flexWrap:"wrap", gap:8, alignItems:"center" }}>
                    {visitHref && (
                      <>
                        <a href={visitHref} target="_blank" rel="noopener noreferrer" title={visitHref} style={{ fontSize: mobile ? 15 : 16, color:C.blue, textDecoration:"underline", textDecorationColor:"rgba(59,130,246,0.4)" }}>Visit Firm</a>
                        <span style={{ color:C.textDim }}>·</span>
                      </>
                    )}
                    <span style={{ fontSize: mobile ? 14 : 15, color:C.textMuted }}>Est. {firm.founded} · {firm.location}</span>
                  </div>
                  {firm.regulated && firm.regulator && <div style={{ fontSize: mobile ? 14 : 15, color:C.textMuted, marginTop:6 }}>{firm.regulator}</div>}
                  <div style={{ fontSize: mobile ? 14 : 15, marginTop:6 }}>
                    <span style={{ color:C.textDim }}>{firm.platforms.length > 1 ? "Platforms:" : "Platform:"}</span>{" "}
                    <span style={{ color:C.text, fontWeight:600 }}>{firm.platforms.join(" · ")}</span>
                  </div>
                </div>
              </div>

              {/* QUICK-GLANCE STATS */}
              <div style={{ display:"grid", gridTemplateColumns:`repeat(${cols}, 1fr)`, rowGap: mobile ? 20 : 28, padding: mobile ? "20px 0" : "28px 0", marginBottom: mobile ? 24 : 32, borderTop:"1px solid rgba(255,255,255,0.06)", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                {stats.map((b, i) => {
                  const isLast = (i % cols) === cols - 1;
                  return (
                    <div key={b.label} style={{ textAlign:"center", padding: mobile ? "0 10px" : "0 24px", borderRight: isLast ? "none" : "1px solid rgba(255,255,255,0.06)" }}>
                      <div style={{ fontSize: mobile ? 10 : 12, textTransform:"uppercase", letterSpacing:"0.1em", color:C.textDim, fontWeight:500, marginBottom:8 }}>{b.label}</div>
                      <div style={{ fontFamily:MONO, fontSize: mobile ? 13 : 16, fontWeight:600, color:badgeStyles[b.variant].color }}>{b.value}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* PRODUCT TABS */}
            {firm.products.length > 1 && (
              <div style={{
                position: "sticky",
                top: mobile ? headerH + 90 : 52,
                zIndex: 48,
                background: "rgba(10,15,28,0.97)",
                borderBottom: "1px solid rgba(59,130,246,0.15)",
                backdropFilter: "blur(12px)",
                margin: mobile ? "0 -16px" : "0 -32px",
                padding: mobile ? "10px 16px 10px" : "12px 32px 12px",
                marginBottom: mobile ? 16 : 20,
                display: "flex",
                justifyContent: "center",
                gap: 8,
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}>
                {firm.products.map((p, i) => (
                  <button key={p.name} onClick={() => { setTab(i); setAiSummaryOpen(false); }} style={{
                    flexShrink: 0,
                    padding: mobile ? "8px 16px" : "9px 20px",
                    background: tab === i ? "rgba(59,130,246,0.12)" : "rgba(255,255,255,0.02)",
                    border: tab === i ? "1px solid rgba(59,130,246,0.45)" : "1px solid rgba(255,255,255,0.07)",
                    borderBottom: tab === i ? "2px solid #3b82f6" : "1px solid rgba(255,255,255,0.07)",
                    borderRadius: 8,
                    color: tab === i ? C.text : C.textMuted,
                    fontSize: mobile ? 13 : 14,
                    fontWeight: tab === i ? 600 : 500,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    whiteSpace: "nowrap",
                    transition: "background 0.15s, border-color 0.15s, color 0.15s",
                    boxShadow: tab === i ? "0 0 12px rgba(59,130,246,0.15)" : "none",
                  }}
                  onMouseEnter={e => { if (tab !== i) { e.currentTarget.style.background = "rgba(59,130,246,0.06)"; e.currentTarget.style.borderColor = "rgba(59,130,246,0.25)"; e.currentTarget.style.color = C.text; } }}
                  onMouseLeave={e => { if (tab !== i) { e.currentTarget.style.background = "rgba(255,255,255,0.02)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"; e.currentTarget.style.color = C.textMuted; } }}
                  >{p.name}</button>
                ))}
              </div>
            )}

            {/* COMPARE BUTTON + AI SUMMARY TOGGLE */}
            {(firm.products.length > 1 || (mobile && summary)) && (
              <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom: mobile ? (summary && aiSummaryOpen ? 0 : 20) : 28, alignItems:"center" }}>
                {firm.products.length > 1 && (
                  <button type="button" onClick={() => setCompareOpen(true)}
                    style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"7px 14px", background:"transparent", border:"1px solid rgba(59,130,246,0.35)", borderRadius:6, color:C.textMuted, fontSize: mobile ? 14 : 15, fontWeight:500, cursor:"pointer", fontFamily:"inherit", transition:"border-color 0.2s, color 0.2s, box-shadow 0.2s", boxShadow:"0 0 8px rgba(59,130,246,0.15), 0 0 20px rgba(59,130,246,0.06)" }}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor=C.blue;e.currentTarget.style.color=C.text;e.currentTarget.style.boxShadow="0 0 12px rgba(59,130,246,0.3), 0 0 30px rgba(59,130,246,0.1)";}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor="rgba(59,130,246,0.35)";e.currentTarget.style.color=C.textMuted;e.currentTarget.style.boxShadow="0 0 8px rgba(59,130,246,0.15), 0 0 20px rgba(59,130,246,0.06)";}}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 3h5v5"/><path d="M8 3H3v5"/><path d="M21 3l-7 7"/><path d="M3 3l7 7"/><path d="M16 21h5v-5"/><path d="M8 21H3v-5"/><path d="M21 21l-7-7"/><path d="M3 21l7-7"/></svg>
                    Compare Account Types
                  </button>
                )}
                {mobile && summary && (
                  <button type="button" onClick={() => setAiSummaryOpen(o => !o)}
                    style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"7px 14px", background:"linear-gradient(135deg, rgba(139,92,246,0.12) 0%, rgba(59,130,246,0.12) 50%, rgba(16,185,129,0.08) 100%)", border:"1px solid rgba(139,92,246,0.25)", borderRadius:20, color:"#c4b5fd", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"inherit", transition:"all 0.2s" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 3v2m0 14v2M5.636 5.636l1.414 1.414m9.9 9.9l1.414 1.414M3 12h2m14 0h2M5.636 18.364l1.414-1.414m9.9-9.9l1.414-1.414" stroke="#a78bfa" />
                      <circle cx="12" cy="12" r="3" stroke="#a78bfa" />
                    </svg>
                    AI Firm Overview
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                      style={{ transform: aiSummaryOpen ? "rotate(180deg)" : "rotate(0deg)", transition:"transform 0.2s" }}>
                      <path d="m6 9 6 6 6-6"/>
                    </svg>
                  </button>
                )}
              </div>
            )}

            {/* MOBILE AI SUMMARY — expanded content */}
            {mobile && summary && aiSummaryOpen && (
              <div style={{ marginBottom:20, padding:"16px 18px", background:"linear-gradient(135deg, rgba(139,92,246,0.06) 0%, rgba(59,130,246,0.06) 50%, rgba(16,185,129,0.06) 100%)", border:"1px solid rgba(139,92,246,0.15)", borderRadius:12, animation:"aiSummaryIn 0.2s ease-out" }}>
                <style>{`@keyframes aiSummaryIn{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}`}</style>
                <p style={{ margin:0, fontSize:14, color:"#9ca3af", lineHeight:1.75 }}>{summary}</p>
              </div>
            )}

            {/* DESKTOP AI OVERVIEW — always visible */}
            {!mobile && summary && (
              <div style={{ marginBottom:32, padding:"20px 24px", background:"linear-gradient(135deg, rgba(139,92,246,0.06) 0%, rgba(59,130,246,0.06) 50%, rgba(16,185,129,0.06) 100%)", border:"1px solid rgba(139,92,246,0.15)", borderRadius:12, position:"relative", overflow:"hidden" }}>
                <div style={{ position:"absolute", top:0, left:0, right:0, height:1, background:"linear-gradient(90deg, rgba(139,92,246,0.4), rgba(59,130,246,0.4), rgba(16,185,129,0.4))" }} />
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 3v2m0 14v2M5.636 5.636l1.414 1.414m9.9 9.9l1.414 1.414M3 12h2m14 0h2M5.636 18.364l1.414-1.414m9.9-9.9l1.414-1.414" stroke="url(#ai-icon-grad)" />
                    <circle cx="12" cy="12" r="4" stroke="url(#ai-icon-grad)" />
                    <defs>
                      <linearGradient id="ai-icon-grad" x1="0" y1="0" x2="24" y2="24">
                        <stop offset="0%" stopColor="#8b5cf6" />
                        <stop offset="100%" stopColor="#3b82f6" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <span style={{ fontSize:12, fontWeight:700, letterSpacing:"0.06em", textTransform:"uppercase", color:"#c4b5fd", background:"linear-gradient(135deg, rgba(139,92,246,0.18) 0%, rgba(59,130,246,0.18) 50%, rgba(16,185,129,0.12) 100%)", padding:"4px 12px", borderRadius:20, border:"1px solid rgba(139,92,246,0.2)", boxShadow:"0 0 10px rgba(139,92,246,0.3), 0 0 22px rgba(59,130,246,0.15), 0 0 40px rgba(16,185,129,0.07)" }}>AI Firm Overview</span>
                </div>
                <p style={{ margin:0, fontSize:15, color:"#9ca3af", lineHeight:1.75 }}>{summary}</p>
              </div>
            )}

            {/* PRICING TABLE */}
            {product.pricing && product.pricing.length > 0 && (
              <div id="section-pricing" style={{ marginBottom: mobile ? 28 : 36 }}>
                <h3 style={{ margin:"0 0 12px", fontSize: mobile ? 21 : 23, fontWeight:700, color:C.text }}>Pricing</h3>
                <div style={{ border:"1px solid rgba(255,255,255,0.06)", borderRadius:10, overflow:"hidden", position:"relative", background:TABLE_PANEL_BG }}>
                  <div style={{ position:"absolute", left:0, top:0, bottom:0, width:3, background:"linear-gradient(180deg, #60a5fa 0%, #1e3a8a 100%)", zIndex:1, borderRadius:"10px 0 0 10px" }} />
                  <div style={{ overflowX:"auto" }}>
                    <table style={{ width:"100%", borderCollapse:"collapse", minWidth:400, background:TABLE_PANEL_BG }}>
                      <thead>
                        <tr style={{ background:TABLE_HEADER_BG }}>
                          <th style={{ padding:"12px 16px", textAlign:"left", fontSize: mobile ? 13 : 14, fontWeight:600, color:C.textDim, textTransform:"uppercase", letterSpacing:"0.04em" }}>Account size</th>
                          {(product.sizes || []).map(s => (
                            <th key={s} style={{ padding:"12px 16px", textAlign:"center", fontSize: mobile ? 14 : 15, fontWeight:700, color:C.text, fontVariantNumeric:"tabular-nums" }}>{s}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {product.pricing.map((r, i) => (
                          <tr key={i} style={{ borderTop:"1px solid rgba(255,255,255,0.06)" }}>
                            <td style={{ padding:"10px 16px", fontSize: mobile ? 13 : 14, color:C.blue, fontWeight:600 }}>{r.label}</td>
                            {r.values.map((v, j) => (
                              <td key={j} style={{ padding:"10px 16px", textAlign:"center", fontSize: mobile ? 14 : 15, fontWeight:500, fontVariantNumeric:"tabular-nums lining-nums", color: r.color || C.text }}>{v}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* EVAL TABLE */}
            {product.evalTable && (
              <div id="section-eval" style={{ marginBottom: mobile ? 28 : 36 }}>
                <TSPhaseTable title={product.evalTable.title} rows={product.evalTable.rows} sizes={product.sizes} accent="linear-gradient(180deg, #60a5fa 0%, #1e3a8a 100%)" />
              </div>
            )}

            {/* FUNDED TABLE */}
            {product.fundedTable && (
              <div id="section-funded" style={{ marginBottom: mobile ? 28 : 36 }}>
                <TSPhaseTable title={product.fundedTable.title} rows={product.fundedTable.rows} sizes={product.sizes} accent="linear-gradient(180deg, #60a5fa 0%, #1e3a8a 100%)" />
              </div>
            )}

            {/* ACCORDION SECTIONS */}
            {sections.length > 0 && (
              <div style={{ display:"flex", flexDirection:"column", gap:12, marginBottom: mobile ? 28 : 36 }}>
                {["Account Info", "Payout Rules", "Trading Restrictions", "Account Killers"]
                  .map(t => sections.find(s => s.title === t))
                  .filter(Boolean)
                  .map(s => {
                    const meta = SECTION_META[s.title] || {};
                    return (
                      <TSCollapsible
                        key={s.title}
                        title={s.title}
                        dot={s.dot}
                        items={s.items}
                        id={`section-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                        forceOpen={forceOpenSection === s.title}
                        subtitle={meta.subtitle}
                        icon={meta.icon}
                      />
                    );
                  })}
              </div>
            )}

          </div>
        </div>
      </div>

      {/* COMPARE MODAL */}
      {compareOpen && firm.products.length > 1 && (() => {
        const products = firm.products;
        const allLabels = [];
        const seen = new Set();
        products.forEach(p => p.params.forEach(([label]) => { if (!seen.has(label)) { seen.add(label); allLabels.push(label); } }));
        const diffRows = allLabels.filter(label => {
          const vals = products.map(p => { const found = p.params.find(([l]) => l === label); return found ? found[1] : "—"; });
          return !vals.every(v => v === vals[0]);
        });
        const getMajority = (vals) => {
          const counts = {};
          vals.forEach(v => { counts[v] = (counts[v] || 0) + 1; });
          const max = Math.max(...Object.values(counts));
          if (Object.values(counts).filter(c => c === max).length === Object.keys(counts).length) return null;
          return Object.keys(counts).find(k => counts[k] === max);
        };
        return (
          <div onClick={() => setCompareOpen(false)} style={{ position:"fixed", inset:0, zIndex:10000, display:"flex", alignItems: mobile ? "flex-start" : "center", justifyContent:"center", background:"rgba(0,0,0,0.45)", backdropFilter:"blur(3px)", animation:"compareFadeIn 0.15s ease-out" }}>
            <style>{`@keyframes compareFadeIn{from{opacity:0}to{opacity:1}} @keyframes compareSlideIn{from{opacity:0;transform:translateY(16px) scale(0.97)}to{opacity:1;transform:translateY(0) scale(1)}} @keyframes compareSlideDown{from{opacity:0;transform:translateY(-20px)}to{opacity:1;transform:translateY(0)}}`}</style>
            <div onClick={e => e.stopPropagation()} style={{
              background:C.card, display:"flex", flexDirection:"column",
              border: mobile ? "none" : "1px solid rgba(255,255,255,0.06)",
              borderRadius: mobile ? 0 : 12,
              width: mobile ? "100%" : "90%", maxWidth: mobile ? "none" : 800,
              height: mobile ? "100vh" : undefined,
              maxHeight: mobile ? "100vh" : "80vh",
              animation: mobile ? "compareSlideDown 0.2s ease-out" : "compareSlideIn 0.2s ease-out",
              boxShadow: mobile ? "none" : "0 24px 80px rgba(0,0,0,0.5)",
            }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding: mobile ? "14px 16px" : "18px 24px", borderBottom:"1px solid rgba(255,255,255,0.06)", flexShrink:0 }}>
                <span style={{ color:C.text, fontSize: mobile ? 15 : 16, fontWeight:700 }}>{firm.name} — Compare Account Types</span>
                <button type="button" onClick={() => setCompareOpen(false)} style={{ background:"rgba(255,255,255,0.08)", border:"none", color:C.textMuted, width:32, height:32, borderRadius:8, cursor:"pointer", fontSize:16, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontFamily:"inherit" }}>✕</button>
              </div>
              <div style={{ flex:1, overflowY:"auto", overflowX:"auto", WebkitOverflowScrolling:"touch", background:TABLE_PANEL_BG }}>
                {diffRows.length === 0 ? (
                  <div style={{ padding:"32px 24px", color:C.textMuted, fontSize:14, textAlign:"center" }}>All parameters are identical across account types.</div>
                ) : (
                  <table style={{ width:"100%", borderCollapse:"collapse", minWidth: mobile ? 420 : undefined, background:TABLE_PANEL_BG }}>
                    <thead>
                      <tr style={{ borderBottom:"1px solid rgba(255,255,255,0.06)", background:TABLE_HEADER_BG }}>
                        <th style={{ padding: mobile ? "10px 12px" : "12px 16px", textAlign:"left", color:C.textDim, fontWeight:600, fontSize: mobile ? 11 : 12, textTransform:"uppercase", letterSpacing:"0.05em", width: mobile ? 110 : 160, minWidth: mobile ? 90 : 120, position:"sticky", top:0, background:TABLE_HEADER_BG, zIndex:1 }}></th>
                        {products.map((p, i) => (
                          <th key={i} style={{ padding: mobile ? "10px 12px" : "12px 16px", textAlign:"left", verticalAlign:"top", position:"sticky", top:0, background:TABLE_HEADER_BG, zIndex:1 }}>
                            <div style={{ color:C.text, fontWeight:700, fontSize: mobile ? 13 : 14 }}>{p.name}</div>
                            <div style={{ color:C.textDim, fontSize: mobile ? 10 : 11, marginTop:2, lineHeight:1.3 }}>{p.badge}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {diffRows.map((label, ri) => {
                        const vals = products.map(p => { const found = p.params.find(([l]) => l === label); return found ? found[1] : "—"; });
                        const majority = getMajority(vals);
                        return (
                          <tr key={ri} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                            <td style={{ padding: mobile ? "10px 12px" : "12px 16px", color:C.blue, fontWeight:600, fontSize: mobile ? 12 : 13 }}>{label}</td>
                            {vals.map((v, ci) => {
                              const isUnique = majority && v !== majority;
                              return (
                                <td key={ci} style={{ padding: mobile ? "10px 12px" : "12px 16px", fontSize: mobile ? 12 : 13, fontWeight: isUnique ? 600 : 400, color: isUnique ? C.blue : C.text, background: isUnique ? "rgba(59,130,246,0.08)" : "transparent" }}>{v}</td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default function FirmsPage() {
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const [search, setSearch] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterDrawdown, setFilterDrawdown] = useState("all");
  const [filterNews, setFilterNews] = useState("all");
  // eslint-disable-next-line no-unused-vars
  const [filterRegulated, setFilterRegulated] = useState("all");
  const filterPanelRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!filterPanelRef.current) return;
      if (!filterPanelRef.current.contains(e.target)) setFilterOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const filtered = FIRMS.filter(f=>{
    if (search) {
      const s = search.toLowerCase();
      const searchable = [
        f.name, f.id, f.tagline, f.bestFor, f.drawdownType, f.profitSplit,
        f.location, f.founded, f.website, f.maxFundedAccts || "",
        f.platforms.join(" "),
        f.regulated ? "NFA regulated" : "",
        f.newsTrading ? "news trading allowed" : "news trading blocked restricted",
        f.overnightAllowed ? "overnight allowed swing" : "no overnight",
        f.hasDLL ? "daily loss limit DLL" : "no daily loss limit no DLL",
        f.passInOneDay ? "pass in one day 1 day pass" : "",
        ...f.products.flatMap(p => [
          p.name, p.badge,
          ...p.params.map(([label, value]) => `${label} ${value}`),
          ...(p.sections || []).flatMap(sec => [
            sec.title,
            ...sec.items.flatMap(item => [item.bold, ...item.lines]),
          ]),
        ])
      ].join(" ").toLowerCase();
      const words = s.trim().split(/\s+/);
      if (!words.every(w => searchable.includes(w))) return false;
    }
    const dd = (f.drawdownType || "").toLowerCase();
    if (filterDrawdown === "eod" && !dd.includes("eod")) return false;
    if (filterDrawdown === "intraday" && !dd.includes("intraday")) return false;
    if (filterNews === "yes" && f.newsTrading !== true) return false;
    if (filterNews === "no" && f.newsTrading !== false) return false;
    if(filterRegulated==="yes" && !f.regulated) return false;
    if(filterRegulated==="no" && f.regulated) return false;
    return true;
  });

  const searchResults = (() => {
    if (search.length < 3) return [];
    const s = search.toLowerCase();
    const words = s.trim().split(/\s+/);
    const matchesAll = (text) => { const t = text.toLowerCase(); return words.every(w => t.includes(w)); };
    const results = [];
    const push = (firmName, firmId, productName, sectionTitle, matchText, matchType) => {
      results.push({ firmName, firmId, productName, sectionTitle, matchText, matchType });
    };
    for (const f of FIRMS) {
      const fInfo = (text) => push(f.name, f.id, null, null, text, "firm-info");
      const firmCtx = f.name.toLowerCase() + " ";
      if (f.tagline && matchesAll(firmCtx + f.tagline)) fInfo(f.tagline);
      if (f.bestFor && matchesAll(firmCtx + f.bestFor)) fInfo(f.bestFor);
      if (f.drawdownType && matchesAll(firmCtx + `Drawdown: ${f.drawdownType}`)) fInfo(`Drawdown: ${f.drawdownType}`);
      if (f.profitSplit && matchesAll(firmCtx + `Profit Split: ${f.profitSplit}`)) fInfo(`Profit Split: ${f.profitSplit}`);
      if (f.maxFundedAccts && matchesAll(firmCtx + `Max Funded: ${f.maxFundedAccts}`)) fInfo(`Max Funded: ${f.maxFundedAccts}`);
      if (f.location && matchesAll(firmCtx + `Location: ${f.location}`)) fInfo(`Location: ${f.location}`);
      f.platforms.forEach(pl => { if (matchesAll(firmCtx + `Platform: ${pl}`)) fInfo(`Platform: ${pl}`); });
      for (const p of f.products) {
        const prodCtx = firmCtx + p.name.toLowerCase() + " ";
        if (p.badge && matchesAll(prodCtx + p.badge)) push(f.name, f.id, p.name, null, p.badge, "badge");
        for (const [label, value] of p.params) {
          const combined = `${label}: ${value}`;
          if (matchesAll(prodCtx + combined)) push(f.name, f.id, p.name, null, combined, "param");
        }
        for (const sec of (p.sections || [])) {
          const secCtx = prodCtx + sec.title.toLowerCase() + " ";
          for (const item of sec.items) {
            const fullText = [item.bold, ...item.lines].join(" ");
            if (matchesAll(secCtx + fullText)) push(f.name, f.id, p.name, sec.title, item.bold, "rule");
          }
        }
      }
    }
    return results;
  })();
  const searchResultsTotal = searchResults.length;
  const searchResultsCapped = searchResults.slice(0, 15);

  const highlightMatch = (text, term) => {
    const words = term.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return <span style={{ color:C.textDim }}>{text}</span>;
    const escaped = words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    const regex = new RegExp(`(${escaped.join("|")})`, "gi");
    const parts = text.split(regex);
    return (
      <span style={{ color:C.textDim }}>
        {parts.map((part, i) =>
          regex.test(part)
            ? <span key={i} style={{ color:C.blue, background:"rgba(59,130,246,0.15)", borderRadius:3, padding:"0 2px" }}>{part}</span>
            : part
        )}
      </span>
    );
  };

  const btnStyle = (active) => ({ padding:"6px 14px", borderRadius:20, border:`1px solid ${active?C.blue:C.border}`, background: active?C.blueDim:"transparent", color: active?C.blue:C.textMuted, cursor:"pointer", fontSize:12, fontWeight:500 });
  const activeFilterCount = [filterDrawdown, filterNews].filter(v => v !== "all").length;

  return (
    <div style={{ minHeight:"100vh", background:PAGE_BG, color:C.text }}>
      <div>
        <div style={{ padding: mobile ? "20px 14px 0" : "40px 40px 0", maxWidth:1400, margin:"0 auto" }}>
          <h1 style={{ margin:0, fontSize: mobile ? 22 : 32, fontWeight:700, color:C.text, letterSpacing:"-0.03em" }}>Live Prop Firm Database</h1>
          <p style={{ margin: mobile ? "8px 0 20px" : "10px 0 32px", color:C.textMuted, fontSize: mobile ? 13 : 16, lineHeight:1.5 }}>
            {FIRMS.length} firms verified from official help centers · April 2026
          </p>

          <div style={{ display:"flex", flexWrap:"wrap", gap: mobile ? 8 : 12, marginBottom: mobile ? 16 : 32, alignItems:"flex-start" }}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder={mobile ? "Search firms, rules, features..." : "Search anything — firms, rules, platforms, features..."}
              style={{ padding:"8px 16px", borderRadius:8, border:`1px solid ${C.border}`, background:PAGE_BG, color:C.text, fontSize: mobile ? 13 : 14, width: mobile ? "100%" : 360, outline:"none", fontFamily:"inherit" }}/>
            <div ref={filterPanelRef} style={{ position:"relative", flex:"1 1 260px", minWidth:220, maxWidth:560 }}>
              <button
                type="button"
                onClick={()=>setFilterOpen(o=>!o)}
                style={{
                  height:36, display:"inline-flex", alignItems:"center", gap:8, padding:"0 14px",
                  borderRadius:999, border:`1px solid ${filterOpen || activeFilterCount>0 ? C.blue : C.border}`,
                  background: filterOpen || activeFilterCount>0 ? C.blueDim : "transparent",
                  color: filterOpen || activeFilterCount>0 ? C.blue : C.textMuted,
                  cursor:"pointer", fontSize:12, fontWeight:600, fontFamily:"inherit",
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="4" y1="21" x2="4" y2="14"></line>
                  <line x1="4" y1="10" x2="4" y2="3"></line>
                  <line x1="12" y1="21" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12" y2="3"></line>
                  <line x1="20" y1="21" x2="20" y2="16"></line>
                  <line x1="20" y1="12" x2="20" y2="3"></line>
                  <line x1="1" y1="14" x2="7" y2="14"></line>
                  <line x1="9" y1="8" x2="15" y2="8"></line>
                  <line x1="17" y1="16" x2="23" y2="16"></line>
                </svg>
                {activeFilterCount>0 ? `Filters (${activeFilterCount})` : "Filters"}
              </button>

              {filterOpen && (
                <div
                  style={{
                    position:"absolute", top:44, left:0, right:0, width:"100%", zIndex:20,
                    background:CARD_BG, border:"1px solid rgba(255,255,255,0.08)", borderRadius:12,
                    boxShadow:"0 16px 40px rgba(0,0,0,0.35)", padding:"16px 20px",
                    display:"flex", flexDirection:"column", gap:12, animation:"filterPanelIn 0.16s ease-out",
                  }}
                >
                  <style>{`@keyframes filterPanelIn{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}`}</style>

                  <div style={{ display:"flex", flexWrap:"wrap", gap:8, alignItems:"center" }}>
                    <span style={{ color:C.textDim, fontSize:12, minWidth:96 }}>Drawdown:</span>
                    {["all","eod","intraday"].map(v=>(
                      <button key={v} style={btnStyle(filterDrawdown===v)} onClick={()=>setFilterDrawdown(v)}>{v==="all"?"All":v.toUpperCase()}</button>
                    ))}
                  </div>

                  <div style={{ display:"flex", flexWrap:"wrap", gap:8, alignItems:"center" }}>
                    <span style={{ color:C.textDim, fontSize:12, minWidth:96 }}>News Trading:</span>
                    {[["all","All"],["yes","Allowed"],["no","Blocked"]].map(([v,l])=>(
                      <button key={v} style={btnStyle(filterNews===v)} onClick={()=>setFilterNews(v)}>{l}</button>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setFilterDrawdown("all");
                      setFilterNews("all");
                    }}
                    style={{
                      alignSelf:"flex-start", marginTop:2, padding:0, background:"none", border:"none",
                      color:C.textMuted, textDecoration:"underline", textUnderlineOffset:3, cursor:"pointer",
                      fontSize:12, fontWeight:500, fontFamily:"inherit",
                    }}
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>
          </div>

          {search.length >= 3 && (
            <div style={{
              background:CARD_BG, border:"1px solid rgba(255,255,255,0.08)", borderRadius:12,
              boxShadow:"0 8px 32px rgba(0,0,0,0.3)", marginBottom:24, maxHeight:400, overflowY:"auto",
              padding:"12px 0", animation:"searchPanelIn 0.15s ease-out", position:"relative",
            }}>
              <style>{`@keyframes searchPanelIn{from{opacity:0}to{opacity:1}}`}</style>
              <button type="button" onClick={()=>setSearch("")} aria-label="Dismiss" style={{
                position:"absolute", top:8, right:12, background:"rgba(255,255,255,0.08)", border:"none",
                color:C.textMuted, width:28, height:28, borderRadius:6, cursor:"pointer", fontSize:14,
                display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"inherit", zIndex:1,
              }}>✕</button>
              {searchResultsCapped.length === 0 ? (
                <div style={{ padding:"20px 20px", textAlign:"center", color:C.textMuted, fontSize:13 }}>
                  No matches found for &lsquo;{search}&rsquo;
                </div>
              ) : (
                <>
                  {searchResultsCapped.map((r, i) => (
                    <div
                      key={i}
                      onClick={() => {
                        navigate(`/firms/${r.firmId}`, {
                          state: {
                            productName: r.productName || null,
                            section: r.sectionTitle || null,
                          },
                        });
                        window.scrollTo(0, 0);
                      }}
                      style={{
                        padding:"10px 20px", cursor:"pointer", borderBottom: i < searchResultsCapped.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none",
                        transition:"background 0.12s",
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      <div style={{ fontSize:13, marginBottom:4, display:"flex", alignItems:"center", gap:4, flexWrap:"wrap" }}>
                        <span style={{ color:C.text, fontWeight:700 }}>{r.firmName}</span>
                        {r.productName && <><span style={{ color:C.textDim }}>›</span><span style={{ color:C.textMuted }}>{r.productName}</span></>}
                        {r.sectionTitle && <><span style={{ color:C.textDim }}>›</span><span style={{ color:C.textMuted }}>{r.sectionTitle}</span></>}
                      </div>
                      <div style={{ fontSize:13, lineHeight:1.5 }}>{highlightMatch(r.matchText, search)}</div>
                    </div>
                  ))}
                  {searchResultsTotal > 15 && (
                    <div style={{ padding:"10px 20px 4px", fontSize:12, color:C.textDim, textAlign:"center" }}>
                      Showing 15 of {searchResultsTotal} results
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div style={{ display:"grid", gridTemplateColumns: mobile ? "1fr" : "repeat(auto-fill, minmax(340px, 1fr))", gap: mobile ? 14 : 24, marginBottom: mobile ? 24 : 48, paddingBottom: mobile ? 16 : 32, alignItems:"stretch" }}>
            {filtered.map(firm=>{
              const stats = [
                { label:"DRAWDOWN", value:shortDrawdown(firm.drawdownType), variant:getStatVariant("DRAWDOWN", firm.drawdownType, firm) },
                { label:"PROFIT SPLIT", value:shortSplit(firm.profitSplit), variant:getStatVariant("PROFIT SPLIT", firm.profitSplit, firm) },
                { label:"DAILY LOSS", value:firm.hasDLL ? "Yes" : "None", variant:getStatVariant("DAILY LOSS", null, firm) },
              ];
              const openFirmCard = () => {
                navigate(`/firms/${firm.id}`);
                window.scrollTo(0, 0);
              };
              const cardVisitHref = getFirmVisitHref(firm);
              return (
              <div
                key={firm.id}
                role="button"
                tabIndex={0}
                onClick={openFirmCard}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openFirmCard();
                  }
                }}
                style={{
                  position:"relative", display:"flex", flexDirection:"column", alignItems:"stretch", textAlign:"left",
                  background:CARD_BG,
                  border:"1px solid rgba(100, 160, 255, 0.07)", borderRadius:14, padding:0,
                  cursor:"pointer", transition:"box-shadow 0.2s, transform 0.2s, border-color 0.2s", userSelect:"none", boxShadow:"none", fontFamily:"inherit", overflow:"hidden",
                }}
                onMouseEnter={e=>{ e.currentTarget.style.borderColor="rgba(30, 64, 175, 0.6)"; e.currentTarget.style.boxShadow="0 4px 32px rgba(88, 166, 255, 0.2), 0 0 60px rgba(30, 64, 175, 0.2)"; e.currentTarget.style.transform="translateY(-2px)"; }}
                onMouseLeave={e=>{ e.currentTarget.style.borderColor="rgba(100, 160, 255, 0.07)"; e.currentTarget.style.boxShadow="none"; e.currentTarget.style.transform="translateY(0)"; }}>

                {/* LEFT ACCENT BAR */}
                <div style={{ position:"absolute", left:0, top:0, bottom:0, width:4, background:"linear-gradient(180deg, #60a5fa 0%, #1e3a8a 100%)", borderRadius:"14px 0 0 14px" }} />

                {/* TOP SECTION */}
                <div style={{ padding: mobile ? "16px 16px 14px 20px" : "24px 24px 20px 28px", display:"flex", alignItems:"flex-start", gap: mobile ? 12 : 16, flex:"1 1 auto" }}>
                  <div style={{ width:48, height:48, flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", background:"linear-gradient(180deg, #1e3a5f 0%, #0f1f3d 100%)", border:`1px solid ${C.border}`, borderRadius:12, overflow:"hidden" }}>
                    <img src={firm.logo} alt="" style={{ width:32, height:32, objectFit:"contain" }} onError={e=>{ e.target.onerror=null; e.target.style.display="none"; e.target.nextSibling.style.display="flex"; }} />
                    <span style={{ display:"none", alignItems:"center", justifyContent:"center", width:"100%", height:"100%", fontFamily:MONO, fontWeight:700, fontSize:18, color:C.text }}>{firm.name.charAt(0)}</span>
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                      <h3 style={{ margin:0, fontSize:18, fontWeight:700, color:C.text, letterSpacing:"-0.01em" }}>{firm.name}</h3>
                      {firm.regulated && <span style={{ fontSize:11, fontWeight:500, background:C.greenBg, color:C.green, padding:"3px 8px", borderRadius:4 }}>NFA</span>}
                    </div>
                    <p style={{ margin:"6px 0 0", fontSize:13, color:C.textMuted, lineHeight:1.5 }}>{firm.tagline}</p>
                    {cardVisitHref && (
                      <a
                        href={cardVisitHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={cardVisitHref}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        style={{ marginTop:8, display:"inline-block", fontSize:13, color:C.blue, textDecoration:"underline", textDecorationColor:"rgba(59,130,246,0.4)" }}
                      >
                        Visit Firm
                      </a>
                    )}
                  </div>
                </div>

                {/* STAT STRIP */}
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:1, background:"rgba(100, 160, 255, 0.06)", flexShrink:0 }}>
                  {stats.map(s=>(
                    <div key={s.label} style={{ background:CARD_BG, padding: mobile ? "10px 8px" : "14px 16px", textAlign:"center" }}>
                      <div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:"0.08em", color:C.textDim, fontWeight:500, marginBottom:6 }}>{s.label}</div>
                      <div style={{ fontFamily:MONO, fontSize: mobile ? 11 : 12, fontWeight:600, color:badgeStyles[s.variant].color, whiteSpace:"nowrap" }}>{s.value}</div>
                    </div>
                  ))}
                </div>

                {/* FOOTER */}
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding: mobile ? "10px 16px 10px 20px" : "14px 24px 14px 28px", background:"rgba(0,0,0,0.25)", borderTop:"1px solid rgba(100, 160, 255, 0.06)", flexShrink:0 }}>
                  <TrustpilotStars rating={firm.trustpilotRating || 0} />
                  <span style={{ fontSize:12, fontWeight:500, color:C.textDim, display:"flex", alignItems:"center", gap:4 }}>
                    View firm
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                  </span>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </div>

    </div>
  );
}

export function FirmDetailRoute() {
  const { firmId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const firm = FIRMS.find((f) => f.id === firmId);
  const initialProductName = location.state?.productName ?? null;
  const initialSection = location.state?.section ?? null;

  if (!firm) {
    return <Navigate to="/firms" replace />;
  }

  return (
    <FirmDetail
      firm={firm}
      onBack={() => {
        navigate("/firms");
        window.scrollTo(0, 0);
      }}
      initialProductName={initialProductName}
      initialSection={initialSection}
    />
  );
}
